/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconPackage,
  IconUser,
  IconBuilding,
  IconDownload,
  IconEye,
} from "@tabler/icons-react";
import ListView from "../../components/common/ListView";
import NoDataFound from "../../components/common/NoDataFound";
import { useEffect, useState } from "react";
import formatDate from "../../utilities/helper";
import toast from "react-hot-toast";
import { utils, writeFile } from "xlsx";
import { T, useLanguage } from "../../context/LanguageContext";

interface SearchResultItem {
  entityType: "user" | "order" | "client";
  entityId: string;
  displayTitle: string;
  subText?: string;
  awbNumber?: string;
  orderName?: string;
  referenceNumber?: string;
  receiverCountryName?: string;
  receiverCityName?: string;
  receiverDistrictName?: string;
  status?: string;
  createdAt?: string;
}

// Helper to get entity icon
const getEntityIcon = (type: string) => {
  switch (type) {
    case "order":
      return <IconPackage size={18} stroke={1.5} className="text-blue-500" />;
    case "user":
      return <IconUser size={18} stroke={1.5} className="text-green-500" />;
    case "client":
      return (
        <IconBuilding size={18} stroke={1.5} className="text-yellow-500" />
      );
    default:
      return <IconPackage size={18} stroke={1.5} className="text-gray-500" />;
  }
};

// Helper to get navigation path
const getEntityPath = (type: string, id: string): string => {
  switch (type) {
    case "order":
      return `/orders/${id}`;
    case "user":
      return `/users/${id}`;
    case "client":
      return `/clients/${id}`;
    default:
      return "/orders";
  }
};

const getStatusBadge = (status: string) => {
  const normStatus = (status || "").toUpperCase();
  switch (normStatus) {
    case "NEW_ORDER":
      return {
        label: "New Order",
        className: "bg-emerald-50 border border-emerald-300 text-emerald-800",
      };
    case "ORDER_GENERATED":
      return {
        label: "Order Generated",
        className: "bg-teal-50 border border-teal-300 text-teal-800",
      };
    case "ORDER_CREATED":
      return {
        label: "Order Created",
        className: "bg-sky-50 border border-sky-300 text-sky-800",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        className: "bg-indigo-50 border border-indigo-300 text-indigo-800",
      };
    case "READY_FOR_PICKUP":
    case "READY_TO_PICK":
      return {
        label: "Ready For Pickup",
        className: "bg-yellow-50 border border-yellow-300 text-yellow-800",
      };
    case "PACKED":
      return {
        label: "Packed",
        className: "bg-violet-50 border border-violet-300 text-violet-800",
      };
    case "DISPATCHED":
      return {
        label: "Dispatched",
        className: "bg-fuchsia-50 border border-fuchsia-300 text-fuchsia-800",
      };
    case "IN_TRANSIT":
      return {
        label: "In Transit",
        className: "bg-cyan-50 border border-cyan-300 text-cyan-800",
      };
    case "OUT_FOR_DELIVERY":
      return {
        label: "Out For Delivery",
        className: "bg-orange-50 border border-orange-300 text-orange-800",
      };
    case "DELIVERED":
      return {
        label: "Delivered",
        className: "bg-lime-50 border border-lime-300 text-lime-800",
      };
    case "UNDELIVERED":
      return {
        label: "Undelivered",
        className: "bg-amber-50 border border-amber-300 text-amber-800",
      };
    case "RETURNED":
      return {
        label: "Returned",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "REVERSE_NEW":
      return {
        label: "Reverse New",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "DELETED":
      return {
        label: "Deleted",
        className: "bg-rose-50 border border-rose-300 text-rose-800",
      };
    case "FAILED":
      return {
        label: "Failed",
        className: "bg-red-50 border border-red-300 text-red-800",
      };
    case "LOST":
      return {
        label: "Lost",
        className: "bg-stone-50 border border-stone-300 text-stone-700",
      };
    case "DRAFT":
      return {
        label: "Draft",
        className: "bg-zinc-50 border border-zinc-300 text-zinc-700",
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        className: "bg-slate-50 border border-slate-300 text-slate-700",
      };
    case "RECEIVE_IN_WAREHOUSE":
      return {
        label: "Receive In Warehouse",
        className: "bg-blue-50 border border-blue-300 text-blue-800",
      };
    case "PICKUP_GENERATED":
      return {
        label: "Pickup Generated",
        className: "bg-green-50 border border-green-300 text-green-800",
      };
    case "RETURN_TO_SHIPPER":
      return {
        label: "Return To Shipper",
        className: "bg-rose-100 border border-rose-300 text-rose-800",
      };
    case "FORWARD_TO_3PL":
      return {
        label: "Forward To 3PL",
        className: "bg-purple-100 border border-purple-300 text-purple-800",
      };
    case "DISPATCHED_TO_3PL":
      return {
        label: "Dispatched To 3PL",
        className: "bg-violet-100 border border-violet-300 text-violet-800",
      };
    case "PICKUP_COLLECTED":
      return {
        label: "Pickup Collected",
        className: "bg-teal-100 border border-teal-300 text-teal-800",
      };
    case "PICKLIST_GENERATED":
      return {
        label: "Picklist Generated",
        className: "bg-indigo-100 border border-indigo-300 text-indigo-800",
      };
    case "PICKED":
      return {
        label: "Picked",
        className: "bg-sky-100 border border-sky-300 text-sky-800",
      };
    case "RECEIVE_INBOUND":
      return {
        label: "Receive Inbound",
        className: "bg-cyan-100 border border-cyan-300 text-cyan-800",
      };
    case "SHELVE":
      return {
        label: "Shelve",
        className: "bg-emerald-100 border border-emerald-300 text-emerald-800",
      };
    case "PENDING":
      return {
        label: "Pending",
        className: "bg-amber-100 border border-amber-300 text-amber-800",
      };
    case "PENDING_STOCK":
      return {
        label: "Pending Stock",
        className: "bg-orange-100 border border-orange-300 text-orange-800",
      };
    default:
      const label = status
        ? status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ")
        : "Unknown";
      return {
        label,
        className: "bg-slate-50 border border-slate-200 text-slate-600",
      };
  }
};

const formatStatus = (status?: string) => {
  if (!status) return ""; // fallback
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper to format timestamp
const timeAgo = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

type SearchData = {
  entityId: string;
};

export default function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { searchResults, searchQuery } = (location.state as {
    searchResults: SearchResultItem[];
    searchQuery: string;
  }) || {
    searchResults: [],
    searchQuery: "",
  };



  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "AWB Number": c.awbNumber,
    Reference: c.referenceNumber,
    Country: c.receiverCountryName,
    City: c.receiverCityName,
    District: c.receiverDistrictName,
    Date: formatDate(c.createdAt),
    Status: formatStatus(c.status),
  });

  const handleResultClick = (item: SearchResultItem) => {
    const path = getEntityPath(
      item.entityType,
      item.entityType === "order" ? item.awbNumber || item.entityId : item.entityId
    );
    navigate(path);
  };

  return (
    <>
      <div className="px-6 pb-4">
        <div className="w-full max-w-full bg-white rounded-3xl overflow-hidden shadow-xs border border-slate-100/80">
          <div className="bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full border-b border-slate-100">
            <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
              Search Results{searchQuery ? ` for "${searchQuery}"` : ""}
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (searchResults.length === 0) {
                    toast.error("No data available to download");
                    return;
                  }

                  const exportData = searchResults.map((item, index) =>
                    mapDataToExport(item, index)
                  );

                  try {
                    const worksheet = utils.json_to_sheet(exportData);
                    worksheet["!cols"] = Object.keys(exportData[0]).map(() => ({ wch: 20 }));
                    worksheet["!rows"] = [{ hpt: 25 }, ...exportData.map(() => ({ hpt: 20 }))];

                    const range = utils.decode_range(worksheet["!ref"] || "A1:Z1");

                    // Apply header styles
                    for (let C = range.s.c; C <= range.e.c; ++C) {
                      const cell = utils.encode_cell({ r: 0, c: C });
                      if (!worksheet[cell]) continue;
                      worksheet[cell].s = {
                        font: {
                          name: "Arial",
                          sz: 14,
                          bold: true,
                          color: { rgb: "FFFFFF" },
                        },
                        fill: { patternType: "solid", fgColor: { rgb: "4A90E2" } },
                        alignment: {
                          horizontal: "center",
                          vertical: "center",
                          wrapText: true,
                        },
                        border: {
                          top: { style: "medium", color: { rgb: "000000" } },
                          bottom: { style: "medium", color: { rgb: "000000" } },
                          left: { style: "medium", color: { rgb: "000000" } },
                          right: { style: "medium", color: { rgb: "000000" } },
                        },
                      };
                    }

                    // Apply row styles
                    for (let R = 1; R <= range.e.r; ++R) {
                      for (let C = range.s.c; C <= range.e.c; ++C) {
                        const cell = utils.encode_cell({ r: R, c: C });
                        if (!worksheet[cell]) continue;
                        worksheet[cell].s = {
                          font: { name: "Arial", sz: 12, color: { rgb: "000000" } },
                          alignment: {
                            horizontal: "left",
                            vertical: "center",
                            wrapText: true,
                          },
                          border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } },
                          },
                        };
                      }
                    }

                    const workbook = utils.book_new();
                    utils.book_append_sheet(workbook, worksheet, "Search Results");
                    writeFile(workbook, "search_results.xlsx");
                    toast.success("Excel downloaded successfully!");
                  } catch (error) {
                    console.error("Excel export failed, exporting basic:", error);
                    const worksheet = utils.json_to_sheet(exportData);
                    const workbook = utils.book_new();
                    utils.book_append_sheet(workbook, worksheet, "Search Results");
                    writeFile(workbook, "search_results.xlsx");
                    toast.success("Excel downloaded successfully!");
                  }
                }}
                className="flex items-center gap-1 p-2 text-sm rounded-xl border bg-white text-[#404040] border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-xs cursor-pointer transition-all duration-150"
                title="Download Excel"
              >
                <IconDownload stroke={1.5} size={20} />
              </button>
            </div>
          </div>

          {/* Table or No Data Branch */}
          {searchResults.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse bg-white table-auto">
                <thead className="text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 bg-[#FCFDF8]">
                  <tr className="text-center text-[11px] font-semibold tracking-wider uppercase select-none">
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[35px] min-w-[35px]">#</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">AWB Number</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">Reference</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">Receiver Country</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">Receiver City</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">Receiver District</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[100px]">Created</th>
                    <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[120px]">Status</th>
                    <th className="px-2 pt-3 pb-2 text-center w-[80px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-700 text-[13px]">
                  {searchResults.map((item, idx) => {
                    return (
                      <tr
                        key={`${item.entityId}-${item.status || idx}`}
                        className="hover:bg-slate-50/50 transition duration-100 text-center"
                      >
                        <td className="py-2 px-1 text-slate-400 font-bold text-center">
                          {idx + 1}
                        </td>
                        <td className="py-2 px-1 text-center font-extrabold text-slate-900 text-[12px] whitespace-nowrap">
                          {item.awbNumber || "-"}
                        </td>
                        <td className="py-2 px-1 text-center font-semibold text-slate-500 text-[12px]">
                          {item.referenceNumber || "-"}
                        </td>
                        <td className="py-2 px-1 text-center font-bold text-slate-800 text-[12px]">
                          {item.receiverCountryName || "-"}
                        </td>
                        <td className="py-2 px-1 text-center font-bold text-[#212121] text-[12px]">
                          {item.receiverCityName || "-"}
                        </td>
                        <td className="py-2 px-1 text-center text-slate-500 font-bold text-[11px]">
                          {item.receiverDistrictName || "-"}
                        </td>
                        <td className="py-2 px-1 text-center text-slate-800 font-extrabold text-[12px] whitespace-nowrap">
                          {formatDate(item.createdAt || "")}
                        </td>
                        <td className="py-2 px-1 text-center">
                          {(() => {
                            const badge = getStatusBadge(item.status || "");
                            return (
                              <span
                                className={`border py-0.5 rounded-full text-[11px] font-extrabold tracking-wide inline-block text-center px-3.5 truncate max-w-full align-middle shadow-2xs ${badge.className}`}
                                data-tooltip={t(badge.label)}
                              >
                                <T>{badge.label}</T>
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-1 text-center">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleResultClick(item)}
                              className="p-1.5 border rounded-full bg-[#F7F6F2] hover:bg-slate-100 transition duration-150 shadow-sm text-slate-600 hover:text-slate-800"
                            >
                              <IconEye size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 bg-white">
              <NoDataFound />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
